/**
 * Utilitários para formatação visual de valores monetários e percentuais
 * As máscaras são apenas visuais e não alteram os valores salvos no banco
 */

/**
 * Formata um valor numérico como moeda brasileira (R$ 1.234,56)
 */
export function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numValue =
    typeof value === 'string'
      ? parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.'))
      : value;

  if (isNaN(numValue)) {
    return '';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numValue);
}

/**
 * Converte um valor formatado de moeda de volta para número
 */
export function parseCurrency(value: string): number {
  if (!value || value.trim() === '') {
    return 0;
  }

  // Remove símbolos de moeda e espaços
  let cleaned = value.replace(/[R$\s]/g, '');

  // Se tem vírgula, assume formato brasileiro (1.234,56)
  if (cleaned.includes(',')) {
    // Remove pontos (separadores de milhar) e substitui vírgula por ponto
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  }

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Converte número para string editável (vírgula como decimal, pt-BR)
 * Permite digitar 0,20 etc. durante a edição
 */
export function numberToEditableString(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).replace('.', ',');
}

/**
 * Formata um valor numérico como percentual (12,34%)
 */
export function formatPercentage(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numValue =
    typeof value === 'string'
      ? parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.'))
      : value;

  if (isNaN(numValue)) {
    return '';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numValue / 100);
}

/**
 * Converte um valor formatado de percentual de volta para número
 * Retorna undefined se o valor estiver vazio para permitir apagar completamente
 */
export function parsePercentage(value: string): number | undefined {
  if (!value || value.trim() === '') {
    return undefined;
  }

  // Remove símbolo de percentual e espaços
  let cleaned = value.replace(/[%\s]/g, '');

  // Se após remover símbolos ainda estiver vazio, retorna undefined
  if (!cleaned || cleaned.trim() === '') {
    return undefined;
  }

  // Se tem vírgula, assume formato brasileiro (12,34%)
  if (cleaned.includes(',')) {
    // Remove pontos (separadores de milhar) e substitui vírgula por ponto
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  }

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? undefined : parsed;
}

/**
 * Converte string de percentual para número (retorna 0 se vazio)
 */
export function parsePercentageToNumber(value: string): number {
  const p = parsePercentage(value);
  return p ?? 0;
}
